"use client";

import { isProduction } from "@/constants";
import React from "react";

/**
 * Event IDs are generated in Twitter Events manager
 * https://ads.x.com > Tools > Events manager
 */

export const XPixelIds = {
  leadAddToWaitlist: "tw-ptdbt-pvs8y",
  leadJoinDiscord: "tw-ptdbt-pvtn4",
} as const;

// @see https://business.x.com/en/help/campaign-measurement-and-analytics/conversion-tracking-for-websites
// "Add to Cart"
// "Add to Wishlist"
// "Added Payment Info"
// "Checkout Initiated"
// "Content View"
// "Custom"
// "Download"
// "Lead"
// "Page View"
// "Product Customization"
// "Purchase"
// "Search"
// "Start Trial"
// "Subscribe";

interface XPixelEventPayload {
  email_address: string;
  // Note: include the phone number in the following format: country code (example: +1, +32, etc.) followed by the phone number. For example, for a US phone number: +11234567890
  phone_number: `+${number}`;
  // Total value of the conversion event (ex: $ value of the transaction in case of a purchase, etc.)
  value: number;
  // ISO 4217 code (e.g. USD, JPY, EUR)
  currency: string;
  // Unique identifier for the event that can be used for deduplication purposes
  conversion_id: string;
  // Text that was searched for on your website
  search_string: string;
  // A string description for additional info
  description: string;
  // X click ID that can be included with any request.
  twclid: string;
  // Status of the sign up or subscription, could be “started” or “completed”
  status: string;
  // Array of JSON Objects representing multiple products/content
  contents: {
    // Category of the product purchased (Google product taxonomy)
    content_type: string;
    // For product catalog users: please pass SKU
    // For all other users: Please pass Global Trade Item Number (GTIN) if available, otherwise pass SKU
    content_id: string;
    // Name of a product or service
    content_name: string;
    // Price of a product or service
    content_price: number;
    // Number of products purchased
    num_items: number;
    // ID associated with a group of product variants
    content_group_id: string;
  }[];
}
type XPixelEventId = (typeof XPixelIds)[keyof typeof XPixelIds];

// Declare the global twq function if it's not already available
// This is typically loaded by the X Pixel base code snippet
declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    twq?: (...args: any[]) => void;
  }
}

/**
 * `useXPixel` is a custom React hook for tracking events using the Twitter Pixel (X Pixel).
 * It provides a `trackEvent` function that sends events to Twitter for tracking purposes.
 *
 * @returns An object containing the `trackEvent` function.
 * @example
 * const { trackEvent } = useXPixel();
 *
 * trackEvent('tw-1234-56789', {
 *   value: 99.99,
 *   currency: 'USD',
 *   order_id: 'ORDER123',
 *   content_ids: ['PRODUCT1'],
 *   content_type: 'product',
 * });
 */
export function useXPixel() {
  const trackEvent = React.useCallback(
    (eventId: XPixelEventId, payload?: Partial<XPixelEventPayload>) => {
      if (!window.twq || !isProduction) {
        console.warn("X Pixel not initialized");
        return;
      }

      console.log("Event:", eventId, "payload", payload);

      window.twq("event", eventId, payload);
    },
    [],
  );

  return { trackEvent };
}

// Example usage:
/*
import { useXPixel } from './useXPixel';

const MyComponent = () => {
  const { trackEvent } = useXPixel({
    pixelId: 'YOUR_PIXEL_ID',
    allowFirstPartyCookies: true,
  });

  const handlePurchase = () => {
    trackEvent('tw-1234-56789', {
      value: 99.99,
      currency: 'USD',
      order_id: 'ORDER123',
      content_ids: ['PRODUCT1'],
      content_type: 'product',
    });
  };

  return (
    <button onClick={handlePurchase}>Complete Purchase</button>
  );
};
*/
